import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Brain, Zap, Users, GraduationCap, Trophy, Star, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import roboHappy from "@/assets/characters/robo-happy-optimized.webp";
import sparkleExcited from "@/assets/characters/sparkle-excited-optimized.webp";
import buddyThinking from "@/assets/characters/buddy-thinking-optimized.webp";
import novaRocket from "@/assets/characters/nova-rocket-optimized.webp";
import rocketClean from "@/assets/characters/rocket-clean-256.png";
import starClean from "@/assets/characters/star-clean-256.png";
import wiseOwl from "@/assets/characters/wise-owl-256.png";
import purpleBot from "@/assets/characters/purple-bot-256.png";
import { useAutoInitialization } from "@/hooks/useAutoInitialization";
import { KidsAiLogo } from "@/components/brand/KidsAiLogo";

const Index = () => {
  // Trigger content generation on page load if needed
  useAutoInitialization();
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-kid-blue/30 via-kid-purple/30 to-kid-pink/30 py-20 px-4 min-h-[80vh] flex items-center">
        {/* Animated brand backdrop — performant, color-token driven */}
        <div className="kid-hero-sheen" aria-hidden />
        <div className="kid-bg" aria-hidden>
          <div className="kid-bg-orb kid-bg-orb--purple" style={{ width: 480, height: 480, top: "-10%", left: "-8%" }} />
          <div className="kid-bg-orb kid-bg-orb--pink" style={{ width: 420, height: 420, top: "20%", right: "-10%", animationDelay: "-10s" }} />
          <div className="kid-bg-orb kid-bg-orb--blue" style={{ width: 380, height: 380, bottom: "-12%", left: "30%", animationDelay: "-20s" }} />
        </div>
        {/* Floating characters — frame the hero, desktop only to avoid overlap */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden hidden md:block">
          <img src={roboHappy} width="128" height="128" className="absolute top-20 left-10 w-32 h-32 animate-float opacity-80" alt="" decoding="async" />
          <img src={sparkleExcited} width="112" height="112" className="absolute top-40 right-20 w-28 h-28 animate-float opacity-80" style={{ animationDelay: '1s' }} alt="" decoding="async" />
          <img src={buddyThinking} width="96" height="96" className="absolute bottom-40 left-20 w-24 h-24 animate-float opacity-80" style={{ animationDelay: '2s' }} alt="" decoding="async" />
          <img src={novaRocket} width="128" height="128" className="absolute bottom-20 right-10 w-32 h-32 animate-float opacity-80" style={{ animationDelay: '1.5s' }} alt="" decoding="async" />
        </div>

        {/* Fixed decorative shapes — pinned to edges so they never cross hero content */}
        <div className="absolute inset-0 pointer-events-none hidden md:block">
          <Star className="absolute top-12 left-12 w-6 h-6 text-kid-yellow/40 fill-kid-yellow/40 animate-float" />
          <Sparkles className="absolute top-24 right-16 w-5 h-5 text-kid-pink/40 animate-float" style={{ animationDelay: '1.2s' }} />
          <Heart className="absolute bottom-16 left-1/3 w-4 h-4 text-kid-red/40 fill-kid-red/40 animate-float" style={{ animationDelay: '2.1s' }} />
          <Star className="absolute bottom-24 right-1/4 w-5 h-5 text-kid-yellow/40 fill-kid-yellow/40 animate-float" style={{ animationDelay: '0.6s' }} />
          <Sparkles className="absolute top-1/3 left-8 w-4 h-4 text-kid-purple/40 animate-float" style={{ animationDelay: '1.8s' }} />
          <Heart className="absolute top-1/2 right-8 w-4 h-4 text-kid-pink/40 fill-kid-pink/40 animate-float" style={{ animationDelay: '2.6s' }} />
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-block mb-8">
            <KidsAiLogo animated className="h-24 md:h-36 w-auto drop-shadow-[0_8px_24px_hsl(var(--kid-yellow)/0.35)]" />
          </div>
          
          <h1 className="text-6xl md:text-8xl font-bubblegum font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-kid-purple via-kid-pink to-kid-yellow bg-clip-text text-transparent">
              Learn AI & Robotics
            </span>
            <br />
            <span className="text-gray-800">The Fun Way! 🚀</span>
          </h1>

          <p className="text-2xl md:text-3xl font-comic text-gray-700 mb-8 max-w-4xl mx-auto leading-relaxed">
            Join thousands of kids on an amazing adventure through
            <span className="font-bold text-kid-purple"> Artificial Intelligence</span>,
            <span className="font-bold text-kid-blue"> Machine Learning</span>, and
            <span className="font-bold text-kid-pink"> Robotics</span>! ✨
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <Link to="/login">
              <Button 
                size="lg" 
                className="text-2xl px-12 py-8 rounded-3xl bg-gradient-to-r from-kid-purple via-kid-pink to-kid-yellow font-bubblegum font-bold shadow-2xl hover:shadow-3xl transform hover:scale-110 hover:-rotate-2 transition-all border-4 border-white"
              >
                Start Free! 🎉
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                size="lg" 
                variant="outline"
                className="text-2xl px-12 py-8 rounded-3xl bg-white/90 backdrop-blur font-bubblegum font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all border-4 border-kid-purple text-kid-purple"
              >
                Login 🔐
              </Button>
            </Link>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-gray-600 font-comic">
            <div className="flex items-center gap-2">
              <Star className="w-6 h-6 text-kid-yellow fill-kid-yellow" />
              <span className="text-lg">10,000+ Happy Kids</span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="w-6 h-6 text-kid-orange fill-kid-orange" />
              <span className="text-lg">500+ Schools</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-kid-red fill-kid-red" />
              <span className="text-lg">100% Fun Guaranteed!</span>
            </div>
          </div>
        </div>
      </section>


      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bubblegum font-bold mb-4 text-gray-800">
              Why Kids Love Us! 💖
            </h2>
            <p className="text-2xl font-comic text-gray-600">
              Learning has never been this much fun!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                character: wiseOwl,
                title: "AI-Powered Learning",
                description: "Every kid learns at their own pace with our smart AI tutor!",
                icon: Brain
              },
              {
                character: starClean,
                title: "Super Fun Games!",
                description: "Earn points, badges, and unlock cool achievements!",
                icon: Zap
              },
              {
                character: purpleBot,
                title: "Parents Love It Too!",
                description: "Track progress and celebrate wins together!",
                icon: Trophy
              }
            ].map((feature, index) => (
              <Card 
                key={index}
                className="relative overflow-hidden p-8 border-4 border-white shadow-2xl transform hover:scale-105 hover:rotate-1 transition-all bg-gradient-to-br from-white to-transparent"
                style={{ backgroundColor: `hsl(var(--kid-${['purple', 'yellow', 'blue'][index % 3]})/10)` }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
                  <img 
                    src={feature.character}
                    width="128"
                    height="128"
                    alt=""
                    className="w-full h-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                
                <div className="relative z-10 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-kid-purple to-kid-pink mb-6 shadow-xl animate-bounce-soft">
                    <feature.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-3xl font-bubblegum font-bold mb-4 text-gray-800">
                    {feature.title}
                  </h3>
                  <p className="text-lg font-comic text-gray-600">
                    {feature.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Choose Your Path Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-kid-purple/10 via-kid-pink/10 to-kid-blue/10 relative overflow-hidden">
        {/* Floating decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-20 w-24 h-24 bg-kid-yellow/20 rounded-full blur-2xl animate-float" />
          <div className="absolute bottom-20 left-20 w-32 h-32 bg-kid-pink/20 rounded-full blur-2xl animate-float" style={{ animationDelay: '1s' }} />
          <Star className="absolute top-20 left-1/4 w-6 h-6 text-kid-yellow/40 fill-kid-yellow/40 animate-float" />
          <Sparkles className="absolute bottom-40 right-1/3 w-8 h-8 text-kid-pink/40 animate-float" style={{ animationDelay: '1.5s' }} />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bubblegum font-bold mb-4 bg-gradient-to-r from-kid-purple via-kid-pink to-kid-blue bg-clip-text text-transparent">
              Choose Your Path! 🚀
            </h2>
            <p className="text-2xl font-comic text-gray-700">
              We support both schools and individual learners
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* For Schools Card */}
            <Card className="relative overflow-hidden p-10 border-4 border-white shadow-2xl transform hover:scale-105 hover:-rotate-1 transition-all bg-gradient-to-br from-kid-blue/10 to-white">
              {/* Decorative character */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
                <img src={wiseOwl} width="128" height="128" alt="" className="w-full h-full object-contain" loading="lazy" decoding="async" />
              </div>

              <div className="relative z-10">
                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-kid-blue to-kid-purple flex items-center justify-center mb-6 mx-auto shadow-xl animate-bounce-soft">
                  <GraduationCap className="w-12 h-12 text-white" />
                </div>
                
                <h3 className="text-4xl font-bubblegum font-bold text-center mb-6 text-gray-800">
                  For Schools 🎓
                </h3>
                
                <ul className="space-y-4 mb-10">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-kid-blue/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-kid-blue font-bold">✓</span>
                    </div>
                    <span className="text-lg font-fredoka text-gray-700">Manage multiple classes and teachers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-kid-blue/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-kid-blue font-bold">✓</span>
                    </div>
                    <span className="text-lg font-fredoka text-gray-700">Bulk student enrollment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-kid-blue/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-kid-blue font-bold">✓</span>
                    </div>
                    <span className="text-lg font-fredoka text-gray-700">Advanced analytics dashboard</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-kid-blue/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-kid-blue font-bold">✓</span>
                    </div>
                    <span className="text-lg font-fredoka text-gray-700">Custom curriculum options</span>
                  </li>
                </ul>
                
                <Link to="/register/school" className="block">
                  <Button 
                    size="lg" 
                    className="w-full h-16 text-xl rounded-2xl bg-gradient-to-r from-kid-blue to-kid-purple font-fredoka font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                  >
                    <GraduationCap className="mr-2 h-6 w-6" />
                    Register Your School 🏫
                  </Button>
                </Link>
              </div>
            </Card>

            {/* For Families Card */}
            <Card className="relative overflow-hidden p-10 border-4 border-white shadow-2xl transform hover:scale-105 hover:rotate-1 transition-all bg-gradient-to-br from-kid-pink/10 to-white">
              {/* Decorative character */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
                <img src={purpleBot} width="128" height="128" alt="" className="w-full h-full object-contain" loading="lazy" decoding="async" />
              </div>

              <div className="relative z-10">
                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-kid-pink to-kid-purple flex items-center justify-center mb-6 mx-auto shadow-xl animate-bounce-soft">
                  <Users className="w-12 h-12 text-white" />
                </div>
                
                <h3 className="text-4xl font-bubblegum font-bold text-center mb-6 text-gray-800">
                  For Families 👨‍👩‍👧
                </h3>
                
                <ul className="space-y-4 mb-10">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-kid-pink/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-kid-pink font-bold">✓</span>
                    </div>
                    <span className="text-lg font-fredoka text-gray-700">Fun, interactive learning for kids</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-kid-pink/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-kid-pink font-bold">✓</span>
                    </div>
                    <span className="text-lg font-fredoka text-gray-700">Parent dashboard to track progress</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-kid-pink/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-kid-pink font-bold">✓</span>
                    </div>
                    <span className="text-lg font-fredoka text-gray-700">Safe, kid-friendly environment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-kid-pink/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-kid-pink font-bold">✓</span>
                    </div>
                    <span className="text-lg font-fredoka text-gray-700">Start free, upgrade anytime</span>
                  </li>
                </ul>
                
                <Link to="/register/individual" className="block">
                  <Button 
                    size="lg" 
                    className="w-full h-16 text-xl rounded-2xl bg-gradient-to-r from-kid-pink to-kid-purple font-fredoka font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                  >
                    <Sparkles className="mr-2 h-6 w-6" />
                    Start Free Trial! 🎉
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative overflow-hidden bg-gradient-to-br from-kid-purple/10 via-kid-pink/10 to-kid-blue/10 py-16 px-4">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <Star className="absolute top-10 left-20 w-8 h-8 text-kid-yellow fill-kid-yellow animate-float" />
          <Sparkles className="absolute bottom-10 right-20 w-10 h-10 text-kid-pink animate-float" style={{ animationDelay: '1s' }} />
          <Heart className="absolute top-1/2 left-1/4 w-6 h-6 text-kid-red fill-kid-red animate-float" style={{ animationDelay: '2s' }} />
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center mb-6">
            <KidsAiLogo animated className="h-20 md:h-24 w-auto" />
          </div>
          
          <p className="text-xl font-comic text-gray-700 mb-4">
            Empowering the next generation with AI literacy ✨
          </p>
          
          <div className="flex items-center justify-center gap-6 mb-6 font-comic text-gray-600">
            <a href="#" className="hover:text-kid-purple transition-colors">About 📖</a>
            <span>•</span>
            <a href="#" className="hover:text-kid-pink transition-colors">Contact 💌</a>
            <span>•</span>
            <a href="#" className="hover:text-kid-blue transition-colors">Privacy 🔒</a>
          </div>
          
          <p className="text-sm font-fredoka text-gray-500">
            © 2025 AI Learning Platform. Made with 💖 for kids everywhere!
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
